import { pick } from 'lodash';

export const isObject = (object: unknown): object is Record<string, unknown> =>
  typeof object === 'object' && !Array.isArray(object) && object !== null;

export const keyExists = (object: any, key = 'id') => {
  if (!object || (!isObject(object) && !Array.isArray(object))) {
    return false;
  }

  if (Object.prototype.hasOwnProperty.call(object, key) && Boolean(object[key])) {
    return true;
  }

  if (Array.isArray(object)) {
    for (let i = 0; i < object.length; i++) {
      const result = keyExists(object[i], key) && Boolean(object[i][key]);

      return Boolean(result);
    }
  }

  for (const k in object) {
    const result = keyExists(object[k], key);

    return Boolean(result);
  }

  return false;
};

export const groupByObjProp = (
  array: unknown[] = [],
  objProp: string,
  secondObjProp: string | null = null
) =>
  array.reduce((acc, obj) => {
    const key = obj[objProp];
    const secondKey = obj[secondObjProp];

    // If the key is not in the accumulator, add it with an empty object
    if (!acc[key]) {
      acc[key] = {};
    }

    // If the secondKey is not provided
    if (!secondObjProp) {
      acc[key] = obj;

      return acc;
    }

    // If secondKey is provided and not in the inner object,
    // add it with an array containing the current object
    if (!secondKey || !acc[key][secondKey]) {
      acc[key][secondKey] = [obj];

      return acc;
    }

    // If the secondKey is already in the inner object, push the current object to the array
    acc[key][secondKey].push(obj);

    return acc;
  }, {});

export const removeNullValuesNestedObject = (object) => {
  Object.entries(object).forEach(([key, value]: [string | number, any]) => {
    if (value && isObjectOrArray(value)) {
      removeNullValuesNestedObject(value);
    }

    if (
      (value && isObjectOrArray(value) && !Object.keys(value).length) ||
      value === null ||
      value === undefined
    ) {
      if (Array.isArray(object)) {
        object.splice(key as number, 1);

        return;
      }

      delete object[key];
    }
  });

  return object;
};

export const isEmptyObject = (object, nested = false) => {
  if (!isObject(object)) {
    return true;
  }

  if (nested) {
    object = removeNullValuesNestedObject(object);
  }

  return Object.keys(object).length === 0;
};

const isObjectOrArray = (object) => typeof object === 'object' && object !== null;

export const deepEqual = (obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2);

export const getKeysFromClass = (className: new () => unknown) => {
  const object = new className();

  return Object.keys(object);
};

export const pickByClass = (object: unknown, className: new () => unknown) => {
  const keys = getKeysFromClass(className);

  return pick(object, keys);
};

export const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};
